import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/RadioGroup';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';
import { getApiErrorMessage } from '@/utils/apiError';
import {
  conflictExtra,
  isConflict,
  useColorProperties,
  useCreateColor,
  usePatchColor,
} from '../api';
import { COLOR_CATEGORIES } from '../types';
import type { ColorCategory, ModelColor, ModelColorPatchInput } from '../types';
import { FieldLabel } from './FieldLabel';

const HEX = /^#[\da-f]{6}$/i;

const schema = Yup.object({
  title: Yup.string().trim().required('Povinné pole'),
  hex: Yup.string().required('Povinné pole').matches(HEX, 'Zadejte HEX ve tvaru #RRGGBB'),
  color_code: Yup.string(),
});

interface ColorFormValues {
  title: string;
  hex: string;
  color_code: string;
  category: ColorCategory;
  properties: string[];
}

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((item) => b.includes(item));

/** Builds a partial PATCH: only fields that differ from the initial values are sent. */
function colorPatch(initial: ColorFormValues, values: ColorFormValues): ModelColorPatchInput {
  const patch: ModelColorPatchInput = {};
  if (values.title.trim() !== initial.title) patch.title = values.title.trim();
  if (values.hex !== initial.hex) patch.hex = values.hex;
  if (values.color_code.trim() !== initial.color_code)
    patch.color_code = values.color_code.trim() || null;
  if (values.category !== initial.category) patch.category = values.category;
  if (!sameSet(values.properties, initial.properties)) patch.properties = values.properties;
  return patch;
}

function fieldErrors(details: unknown): Partial<Record<keyof ColorFormValues, string>> {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return {};
  const join = (value: unknown) => (Array.isArray(value) ? value.join(', ') : String(value));
  const source = details as Record<string, unknown>;
  const result: Partial<Record<keyof ColorFormValues, string>> = {};
  (['title', 'hex', 'color_code', 'category', 'properties'] as const).forEach((key) => {
    if (source[key]) result[key] = join(source[key]);
  });
  return result;
}

export function ColorFormDialog({
  modelId,
  initial,
  onClose,
  onSaved,
  returnFocus,
}: {
  modelId: string;
  initial?: ModelColor;
  onClose: () => void;
  onSaved: (id: string) => void;
  returnFocus?: () => void;
}) {
  const editing = !!initial;
  const initialValues: ColorFormValues = {
    title: initial?.title ?? '',
    hex: initial?.hex ?? '#8c969d',
    color_code: initial?.color_code ?? '',
    category: initial?.category ?? 'BA',
    properties: initial?.properties.map((item) => item.id) ?? [],
  };
  const [conflict, setConflict] = useState<{
    input: ModelColorPatchInput;
    appearances: { id: string; title: string }[];
  } | null>(null);
  const properties = useColorProperties();
  const create = useCreateColor();
  const patch = usePatchColor();

  const save = async (input: ModelColorPatchInput) => {
    const saved = await patch.mutateAsync({ id: initial!.id, input });
    toast.success(`Barva ${saved.code} byla uložena`);
    onSaved(saved.id);
  };

  const formik = useFormik<ColorFormValues>({
    initialValues,
    validationSchema: schema,
    onSubmit: async (values, helpers) => {
      try {
        if (editing) {
          const input = colorPatch(initialValues, values);
          if (!Object.keys(input).length) {
            onClose();
            return;
          }
          await save(input);
        } else {
          const created = await create.mutateAsync({
            model: modelId,
            title: values.title.trim(),
            hex: values.hex,
            color_code: values.color_code.trim() || null,
            category: values.category,
            properties: values.properties,
          });
          toast.success(`Barva ${created.code} byla vytvořena`);
          onSaved(created.id);
        }
      } catch (error) {
        if (isConflict(error)) {
          setConflict({
            input: colorPatch(initialValues, values),
            appearances: conflictExtra(error).appearances ?? [],
          });
          return;
        }
        const details = (error as any)?.response?.data?.details;
        helpers.setErrors(fieldErrors(details));
        toast.error(getApiErrorMessage(error));
      }
    },
  });
  const { values, errors, touched, setFieldValue } = formik;
  const saving = create.isLoading || patch.isLoading;

  const confirmShared = async () => {
    const input = conflict!.input;
    setConflict(null);
    try {
      await save({ ...input, confirm_shared: true });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open && !conflict) onClose();
        }}
      >
        <DialogContent
          className="sm:max-w-xl"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            returnFocus?.();
          }}
        >
          <DialogHeader>
            <DialogTitle>{editing ? `Upravit barvu ${initial.code}` : 'Nová barva'}</DialogTitle>
            <DialogDescription>
              Barva patří do palety modelu a vzhledy se na ni odkazují. Číslo (B1, B2, …) přiděluje
              systém.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="text-neutral-800">
            <form id="color-form" onSubmit={formik.handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <FieldLabel label="Název barvy" error={touched.title && errors.title}>
                  <Input
                    name="title"
                    value={values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Např. Antracit"
                    data-testid="color-title-input"
                  />
                </FieldLabel>
                <FieldLabel label="Kód barvy" error={errors.color_code}>
                  <Input
                    name="color_code"
                    value={values.color_code}
                    onChange={formik.handleChange}
                    placeholder="RAL 7016, NCS …"
                    data-testid="color-code-input"
                  />
                </FieldLabel>
                <FieldLabel label="HEX" error={touched.hex && errors.hex}>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      aria-label="Vybrat barvu"
                      className="h-9 w-12 shrink-0 cursor-pointer rounded border border-neutral-200 bg-white p-1"
                      value={HEX.test(values.hex) ? values.hex : '#000000'}
                      onChange={(event) => setFieldValue('hex', event.target.value)}
                    />
                    <Input
                      name="hex"
                      value={values.hex}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="font-mono uppercase"
                      data-testid="color-hex-input"
                    />
                  </div>
                </FieldLabel>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <fieldset>
                  <legend className="text-xs font-medium mb-2">Kategorie</legend>
                  <RadioGroup
                    value={values.category}
                    onValueChange={(value) => setFieldValue('category', value)}
                    className="gap-2"
                  >
                    {COLOR_CATEGORIES.map((item) => (
                      <label key={item.value} className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value={item.value} />
                        {item.label}
                      </label>
                    ))}
                  </RadioGroup>
                  {errors.category && (
                    <p className="text-xs text-red-600 mt-1">{errors.category}</p>
                  )}
                </fieldset>
                <fieldset>
                  <legend className="text-xs font-medium mb-2">Vlastnosti</legend>
                  <div className="space-y-2">
                    {(properties.data ?? []).map((item) => (
                      <label key={item.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={values.properties.includes(item.id)}
                          onCheckedChange={(checked) =>
                            setFieldValue(
                              'properties',
                              checked
                                ? [...values.properties, item.id]
                                : values.properties.filter((id) => id !== item.id)
                            )
                          }
                        />
                        {item.title}
                      </label>
                    ))}
                    {properties.isSuccess && !properties.data.length && (
                      <p className="text-xs text-neutral-500">
                        Žádné vlastnosti nejsou k dispozici.
                      </p>
                    )}
                  </div>
                </fieldset>
              </div>
            </form>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} data-testid="color-form-cancel-btn">
              Storno
            </Button>
            <Button
              type="submit"
              form="color-form"
              disabled={saving}
              data-testid="color-form-submit-btn"
            >
              {editing ? 'Uložit změny' : 'Vytvořit barvu'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {conflict && (
        <AlertDialog open>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Barvu používá více vzhledů</AlertDialogTitle>
              <AlertDialogDescription>
                Změna barvy se projeví ve všech vzhledech, které ji používají.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="px-4 py-3">
              <ul className="space-y-1 text-sm">
                {conflict.appearances.map((entry) => (
                  <li key={entry.id}>{entry.title}</li>
                ))}
              </ul>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConflict(null)}>Storno</AlertDialogCancel>
              <AlertDialogAction onClick={confirmShared}>Změnit ve všech</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
